/**
 * @fileoverview Order details component for comprehensive order editing and management.
 * Provides the main editing interface for orders including field validation, status updates,
 * delivery management, notes editing, and integration with various workflow dialogs.
 * Handles real-time collaboration and change tracking.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable eqeqeq */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Button, TextField, Grid, Typography, InputAdornment, IconButton, Alert, LinearProgress, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import { Storage, FmdBadOutlined, SavedSearchOutlined } from '@mui/icons-material';
import { useRecordContext, useNotify, useDataProvider, Confirm } from 'react-admin';
import { DeliveryMethodColors, DeliveryMethodEnum, STATUS_ENUM, deliveryCongig, getTagsColor } from '../../../../utils/enum';
import dayjs from 'dayjs';
import MDatePicker from '../../../../components/controls/MDatePicker';
import MAutoComplete from '../../../../components/controls/MAutoComplete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import MSelect from '../../../../components/controls/MSelect';
import OrderNotesDetails from './order-notes-details';
import OrderShipping from './shipping';
import DeliveryDelayPopup from './dialogs/delivery-delay';
import SuggestAccountsPopup from './dialogs/suggest-accounts';
import TrackingMappingPopup, { onConfigTrackingMapping } from './dialogs/tracking-mapping';
import OrderProcessTrackingPopup, { onConfigTrackingProcess } from './dialogs/order-process-tracking';
import TrackingProblemPopup, { onConfigTrackingProblem } from './dialogs/tracking-problem';
import MoreDetailPopup from './dialogs/more-detail';
import DMQPopup from './dialogs/dmq';
import MNumber from '../../../../components/controls/MNumber';
import ProofAcceptance from './proof-acceptance';
import { OrderContext } from '../hooks/OrderContext';
import { extractTextFromReactNode, isValidEmail } from '../../../../utils/common';

interface IData {
    [key: string]: any;
}

/**
 * Order details component for comprehensive order management and editing.
 * Provides the main interface for editing order properties, managing workflows,
 * and handling real-time collaboration with other users.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.accounts - Available order accounts for selection
 * @param {string} props.currUser - Current user identifier
 * @param {Object} props.appIO - Application IO configuration for real-time features
 * @param {Object} props.appIO.vendor - Vendor configuration object
 * @param {Object} props.appIO.bus - Event bus for communication
 * @param {Object} props.appIO.bus_events - Event type constants
 * @param {Object} props.effectData - Effect data for UI state management
 * @param {Function} props.setEffectData - Function to update effect data
 * @param {Array} props.filterTags - Available tags for filtering and selection
 * @param {Array} props.marketType - Available market types
 * @returns {JSX.Element} Comprehensive order editing interface
 */
const OrderDetails = React.memo(React.forwardRef<any, IData>((props, ref) => {
    const { accounts, currUser, appIO, effectData, setEffectData, filterTags, marketType, processingState, editingOrders } = props;
    const { vendor, bus, bus_events } = appIO;
    let { isAlert, showOrderLastChangedBy, notes } = effectData;

    const { isResale, usesStrOrderNumber } = useContext(OrderContext);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    let record = useRecordContext();
    if (!record) return null;

    const [orderData, setOrderData] = useState<IData>(record);
    const [forceRender, setForceRender] = useState<IData>({
        isWillCall: false,
        isAvailableUnshipped: false,
        isHardTicket: false,
        isUploadProof: false
    });
    const [initialOrder, setInitialOrder] = useState<any>(null);
    const [customer, setCustomer] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    /**
     * Controls visibility of customer information for Direct Transfer and Flash orders.
     * Customer info is hidden until shipping button is pressed for delivery methods 4 and 5.
     */
    const [showCustomerInfo, setShowCustomerInfo] = useState(true);
    const allowEditStatuses = vendor.getEditableStatuses();
    const [dlgState, setDlgState] = useState<IData>({
        // for delivery delay dialog
        d_isOpen: false,
        d_isEnable: false,
        d_data: {},
        // for tracking mapping dialog
        m_isOpen: false,
        m_data: {},
        // for tracking history dialog
        h_isOpen: false,
        h_isEnable: false,
        h_isSaveOrder: false,
        h_isManual: true, // if tracking history exist or not, if not exist, show manual entry button; if exist, show tracking history button
        // for suggest accts dialog
        s_isOpen: false,
        // for More dialog
        more_isOpen: false,
        // for problem tracking
        btn_problemTracking: false,
        // for validate form
        open: false,
        content: [], // warning content
        control: '', // specify control to do the next steps
        openShipping: false, //for opening Shipping popup or not
        openProof: false, //for opening Proof-Acceptance popup or not
        sendEmail: true, // notify changed or not. Default is sending email if change Delivery Method
        valid_pa_email: true,// check valid primary acc email format
        // for "Unable To Process" tag dialog
        unable_to_process_isOpen: false,
        unable_to_process_addTag: true, // checkbox state - default checked
        // for callback function
        callback: null,
        //reload order
        reload_order: false,
        // Original Order Input: use for call onSaveOrder from outside
        orderView: null,
        // Flag to indicate useeffect to save order from the start: using onSaveOrder
        needsSave: false
    });

    const [isSaving, setSaving] = useState(false);
    const refbtnSave = useRef<any>(null);
    const saveOrderDeferredRef = useRef<any>(null);
    const unableToProcessTag = 'Unable To Process';

    React.useImperativeHandle(ref, () => ({
        onSaveOrder,
        setIsLoading
    }));

    // Call saveOrder from beginning if dlgState.needsSave is true and sure the orderData is latest
    useEffect(() => {
        if (dlgState.needsSave) {
            onSaveOrder(); // no input = save action happens in page details, no need to wait for promise
            setDlgState(prev => ({ ...prev, needsSave: false }));
        }
    }, [dlgState.needsSave]);

    // Update orderData when StatusField component changes internal_status and tags
    useEffect(() => {
        if (editingOrders) {
            let currentEditingOrder = editingOrders.find((o: any) => o.orderNumber === orderData.order_number);
            if (currentEditingOrder) {
                setOrderData(prev => ({ ...prev, tags: record.tags, internal_status: record.internal_status }) );
            }
        }
    }, [processingState.reloadState]);

    React.useEffect(() => {
        setIsLoading(true);

        window.currentOrder = orderData;
        //get order details
        dataProvider.fetchData(vendor.controller, `Get?orderNumber=${orderData.order_number}`)
            .then((rs: any) => {
                setIsLoading(false);
                const order = rs.data;
                if (order) {
                    updateOrderData(order);
                    setInitialOrder(order);//create oldORDER + force render for new order
                    setCustomer(order.customer);

                    // Initialize customer visibility based on delivery method
                    // Hide customer info for Direct Transfer (5) and Flash (4) orders until shipping
                    const isDirectTransferOrFlash = order.delivery_method === 4 || order.delivery_method === 5;
                    setShowCustomerInfo(!isDirectTransferOrFlash);
                }
            })
            .catch((error: any) => {
                notify(`Load order failed: Status ${error.status}, Error ${error}`, { type: 'error', autoHideDuration: 2000 });
            }).finally(() => setIsLoading(false));

        //Reset load notes
        setEffectData((prev) => ({ ...prev, notes: null }));
        //get notes
        loadNotes();

        function updateOrderData (data) {
            let newData = { ...data };
            Object.keys(data).forEach(key => {
                if (key === 'order_notes') newData[key] = data[key]?.slice(0, 3);
                else newData[key] = data[key];
            });
            //Always update value for currentStatus
            newData.currentStatus = newData.internal_status;
            // update record context
            bus.broadcast(bus_events.order_fetch_newdata, newData);
            setOrderData(newData);
        }
    }, [dlgState.reload_order]);

    useEffect(() => {
        if (initialOrder && orderData) config();
    }, [initialOrder, orderData.internal_status, orderData.delivery_method, orderData.primary_account, orderData.uploadProof, orderData.orderProcessTracking]);

    function loadNotes (isRefresh = false) {
        let _record = window.currentOrder || orderData;
        let funcLoadNote = (vendor.checkIfNeedSyncNote() ?? false) ? 'NotesWithSync' : 'Notes';
        dataProvider.fetchData(vendor.controller, `${funcLoadNote}/?order_id=${orderData.id}`)
            .then((rs: any) => {
                if (rs.data) {
                    setEffectData((prev) => ({ ...prev, notes: rs.data }));
                    if (isRefresh) {
                        _record.order_notes = rs.data.reduce(function (prev: any, cur: any) { if (prev.length < 3 && cur.note_type === 0) prev.push(cur); return prev; }, []);
                        _record.order_fullnotes = rs.data;
                        // update order_notes for order record
                        setOrderData((prev) => ({ ...prev, order_notes: _record.order_notes }));
                        bus.broadcast(bus_events.order_note_saved, _record);
                        record.order_notes = _record.order_notes; // update for record context to re-render notes component
                    }
                } else {
                    notify(`Server error - Notes, code=${rs?.status}, message=${rs?.message || 'undefined'}`, { type: 'error', autoHideDuration: 4000 });
                    setEffectData((prev) => ({ ...prev, notes: [] }));
                }
            })
            .catch((error: any) => {
                notify(`Load notes failed: Status ${error.status}, Error ${error}`, { type: 'error', autoHideDuration: 4000 });
                setEffectData((prev) => ({ ...prev, notes: [] }));
            })
            .finally(() => {
                // After reload notes => Always turn off isSaveNote flag and clear all note related data
                setOrderData((prev) => ({ ...prev, isSaveNote: false, newNote: undefined }) );
            });
    }
    /**
     * Config to show/hide a control, set state, other,...
     */
    function config () {
        // config show/hide control
        setForceRender((prev) => ({
            ...prev,
            isAvailableUnshipped: isAvailableUnshipped(),
            isHardTicket: isHardTicket(),
            isWillCall: isWillCall(),
            isUploadProof: isUploadProof()
        }));
        // config delivery delay
        toggleDeliveryDelayBtn();
        // config process tracking
        let _config = onConfigTrackingProcess(initialOrder, orderData, notes, countProcessingAttempts(notes));
        if (_config) setDlgState((prev) => ({ ...prev, h_isEnable: _config.isShow, h_isSaveOrder: false, h_isManual: !_config.isExist }));
        // Config problem tracking
        let isShow = onConfigTrackingProblem(initialOrder, orderData.tags);
        setDlgState((prev) => ({ ...prev, btn_problemTracking: isShow }));
        // config tracking current order
        window.currentOrder = orderData;
    }

    //#region For dialogs

    const onCloseDialog = (event, dialogName, isReject = true) => {
        let name = event?.currentTarget?.ariaLabel || dialogName;
        setDlgState((prev) => ({ ...prev, [name]: false, content: name == 'open' ? [] : prev.content, orderView: null }));
        if (isReject) setSaving(false); // close dialog without saving
        // Check for saveOrderDeferredRef to reject the promise when user cancels the dialog
        if (saveOrderDeferredRef.current) {
            // Complete the Promise with "cancelled" status
            const result = {
                success: false,
                status: 'USER_CANCELLED',
                message: 'User cancelled the save operation.'
            };
            saveOrderDeferredRef.current.resolve(result);
        }
    };

    function isAvailableUnshipped () { return parseInt(orderData?.internal_status) === STATUS_ENUM.PendingShipment; }
    function isHardTicket () { return orderData?.delivery_method === 1; }
    function isWillCall () { return orderData?.delivery_method === 6; }
    function isUploadProof () { return orderData?.uploadProof && parseInt(orderData.internal_status) === STATUS_ENUM.Completed; }
    function isUploadPOA () { return isUploadProof() && initialOrder.event_market !== 'Exact'; }

    function toggleDeliveryDelayBtn () {
        if (!isHardTicket() && isAvailableUnshipped()) {
            setDlgState((prev) => ({ ...prev, d_isEnable: true }));
        } else {
            setDlgState((prev) => ({ ...prev, d_isEnable: false }));
        }
    }

    const onClickDeliveryDelay = (val, name) => {
        // Set delay dete
        let eventDate = dayjs(orderData.date);
        let delayDate = eventDate.subtract(val || 0, 'days');
        let delayFormat = delayDate.format('MM/DD/YYYY');
        onChangeOrder('delay_date', delayDate.format('YYYY-MM-DDT00:00:00'));
        if (isDelayDateInvalid(delayFormat)) {
            alertDelayDate();
            return;
        }
        setDlgState((prev) => ({ ...prev, [name]: true, d_data: { delay_date: delayFormat, days: val } }));
    };

    const sharedDialogProps = React.useMemo(() => ({
        vendor,
        dataProvider,
        data: { initialOrder, order: orderData, dlgState },
        setDlgState,
        setOrderData,
        onClose: onCloseDialog
    }), [dlgState]);
    //#endregion

    //#region validating
    function SkipNotify (exchange, from, to) {
        let delivery_skipped = exchange.delivery_skipped, delivery_valid = exchange.delivery_valid;
        from = parseInt(from);
        to = parseInt(to);
        //Check Valid
        if (!!delivery_valid) {
            let valid = delivery_valid.some(s => (s.from_to[0] === -1 || s.from_to[0] === from) && s.from_to[1] === to);
            console.log(`Valid [${from}, ${to}]: ` + valid);
            if (!valid) return !valid;
        }

        //Check Skipped
        return delivery_skipped.some(s => (s.to === to && s.from.some(f => f === from)) ||
            (!!s.parallel && s.from_to.every(d => [from, to].includes(d))));
    }
    function warnPurchase () {
        if (orderData.processor === 'AP') return false;
        return orderData.purchase_section?.toLowerCase() !== orderData.section?.toLowerCase()
            || (!initialOrder.isRowSpec && orderData.purchase_row !== orderData.row)
            || orderData.purchase_quantity !== orderData.quantity;
    }
    const validateDialogFixed = (control) => {
        let displayed = false;

        //Support VV/TN
        //delivery method changed
        let curOrder = initialOrder;
        let deliveryMethod = orderData.delivery_method;
        let oldDeliveryMethod = curOrder.delivery_method;

        let exchange = deliveryCongig.EXCHANGEs.find((s: any) => s.market_id === curOrder.market_id);
        if (!!exchange && oldDeliveryMethod !== deliveryMethod) {
            let message = `Change delivery from ${deliveryCongig.DELIVERY_MAP[oldDeliveryMethod]} to ${deliveryCongig.DELIVERY_MAP[deliveryMethod]}?   `;
            if (!SkipNotify(exchange, oldDeliveryMethod, deliveryMethod)) {
                displayed = true;
                setDlgState(prev => ({
                    ...prev, content: [...prev.content,
                        <li style={{ color: '#E74C3C' }}>
                            {message}&nbsp;
                            {/* <MCheckbox value={dlgState.sendEmail} id='sendEmail' onChange={(value) => setDlgState(prev => ({ ...prev, sendEmail: value }))} /> */}
                        </li>
                    ]
                }));
            }
            else console.log(`Skipped! ${message}`);
        }
        //cost changed
        let cost = orderData.primary_cost;
        let orderTotal = curOrder.order_total;
        if (!!cost && parseFloat(cost) > parseFloat(orderTotal)) {
            displayed = true;
            let overPPE = parseFloat(cost) > parseFloat(orderTotal) * 1.5;
            let costMessage = `Primary cost too high. PPE ${(overPPE ? 'over' : 'under')} 1.5.`;

            setDlgState(prev => ({
                ...prev, content: [...prev.content,
                    <li style={{ color: overPPE ? '#E74C3C' : '' }}>{costMessage}</li>
                ]
            }));
        }
        else {
            let internal_status = orderData.internal_status;
            if (parseInt(internal_status) === STATUS_ENUM.PendingShipment) {
                if (warnPurchase()) {
                    displayed = true;
                    setDlgState(prev => ({
                        ...prev, content: [...prev.content,
                            <li>Section, row don't match with order data</li>
                        ]
                    }));
                }
            } else if (parseInt(internal_status) === STATUS_ENUM.Canceled) {
                displayed = true;
                setDlgState(prev => ({
                    ...prev, content: [...prev.content,
                        <li style={{ color: '#E74C3C' }}>Tix relisted?<br /><i style={{ color: '#4682B4' }}>Penalty needs to be waived (if secondary seller fault, dm change, cust sabatage, rep fault)? </i></li>
                    ]
                }));
            }
        }
        setDlgState(prev => ({ ...prev, open: displayed, control: control }));
        return displayed;
    };

    function validPurchase () {
        let event_market = orderData.event_market || '';
        let section = orderData.section;
        let row = orderData.row;
        let validateFields = [
            { field: 'primary_order_number', displayName: 'Primary Order Number' },
            { field: 'primary_account', displayName: 'Primary Account' },
            { field: 'primary_cost', displayName: 'Primary Cost' },
            { field: 'purchase_section', displayName: 'Purchase Section' },
            { field: 'purchase_quantity', displayName: 'Purchase Quantity' }
        ];

        if (event_market.toLowerCase() === 'exact') {
            validateFields.push({ field: 'link_to_print_tickets', displayName: 'Link to Print Tickets' });
        }

        if (section.toUpperCase() !== 'GA' && row.toUpperCase() !== 'GA') {
            validateFields.push({ field: 'purchase_row', displayName: 'Purchase Row' });
            validateFields.push({ field: 'lo_seat', displayName: 'Low Seat' });
            validateFields.push({ field: 'hi_seat', displayName: 'High Seat' });
        }

        const result = validateFields.reduce((acc: any, { field, displayName }: any) => {
            const isFieldValid = orderData[field] != null && String(orderData[field]).trim().length > 0;
            if (!isFieldValid) {
                acc.invalidFields.push({ field, displayName });
                acc.isValid = false;
            }
            return acc;
        }, { isValid: true, invalidFields: [] });

        if (!result.isValid) {
            notify(
                <Alert severity="error">
                    <span style={{ fontWeight: 'bold' }}>Invalid Input!</span><br />
                    Please input the following required fields: <br />
                    {result.invalidFields.map(({ field, displayName }) => (
                        <span key={field} style={{
                            display: 'block',
                            color: '#E74C3C',
                            fontSize: '13px',
                        }}>
                            - {displayName}
                        </span>
                    ))}
                </Alert>, { autoHideDuration: 15000 }
            );
        }

        return result.isValid;
    }

    function validComplete () {
        let validateFields = [
            { field: 'primary_order_number', displayName: 'Primary Order Number' },
            { field: 'primary_account', displayName: 'Primary Account' },
            { field: 'primary_cost', displayName: 'Primary Cost' }
        ];

        let tags = orderData.tags;
        //Skipped for canceled event
        if (tags.toLowerCase().includes('canceled event')) return true;

        const result = validateFields.reduce((acc: any, { field, displayName }: any) => {
            const isFieldValid = orderData[field] != null && String(orderData[field]).trim().length > 0;
            if (!isFieldValid) {
                acc.invalidFields.push({ field, displayName });
                acc.isValid = false;
            }
            return acc;
        }, { isValid: true, invalidFields: [] });

        if (!result.isValid) {
            notify(
                <Alert severity="error">
                    <span style={{ fontWeight: 'bold' }}>Invalid Input!</span><br />
                    Please input the following required fields: <br />
                    {result.invalidFields.map(({ field, displayName }) => (
                        <span key={field} style={{
                            display: 'block',
                            color: '#E74C3C',
                            fontSize: '13px',
                        }}>
                            - {displayName}
                        </span>
                    ))}
                </Alert>, { autoHideDuration: 15000 }
            );
        }
        return result.isValid;
    }

    /**
     * Validates that Primary Cost and Primary Account fields are populated when changing
     * order status from Processing to Unprocessed.
     *
     * Business Rule: When an administrator attempts to change an order's status from
     * "Processing" (6) to "Unprocessed" (0), both Primary Cost and Primary Account must
     * be provided to ensure proper order tracking and accountability.
     *
     * @returns {boolean} True if validation passes (fields are populated or status change doesn't require validation), false otherwise
     */
    function validProcessingToUnprocessed () {
        // Only validate when changing from Processing to Unprocessed
        const currentStatus = parseInt(orderData.internal_status);
        const previousStatus = parseInt(initialOrder?.internal_status);

        let isProcessing = [STATUS_ENUM.Processing, STATUS_ENUM.TNProcessing].includes(previousStatus);
        // More additional tags to skip validation
        const SKIP_TAGS = ['Waiting to Process', 'Problem', 'Offered Alts', 'Order Support'];
        const currentTags = orderData.tags ? orderData.tags.split(',').map((tag: any) => tag.trim()).filter((tag: any) => tag) : [];
        if (!isProcessing || currentTags.some(tag => SKIP_TAGS.includes(tag)) || currentStatus !== STATUS_ENUM.Unprocessed) {
            return true; // Validation not required for other status changes
        }

        // Validate required fields: Primary Cost and Primary Account
        const validateFields = [
            { field: 'primary_cost', displayName: 'Primary Cost' },
            { field: 'primary_account', displayName: 'Primary Account' }
        ];

        const result = validateFields.reduce((acc: any, { field, displayName }: any) => {
            const isFieldValid = orderData[field] != null && String(orderData[field]).trim().length > 0;
            if (!isFieldValid) {
                acc.invalidFields.push({ field, displayName });
                acc.isValid = false;
            }
            return acc;
        }, { isValid: true, invalidFields: [] });

        if (!result.isValid) {
            notify(
                <Alert severity="error">
                    <span style={{ fontWeight: 'bold' }}>Invalid Input!</span><br />
                    Cannot change status from Processing to Unprocessed without the following required fields: <br />
                    {result.invalidFields.map(({ field, displayName }) => (
                        <span key={field} style={{
                            display: 'block',
                            color: '#E74C3C',
                            fontSize: '13px',
                        }}>
                            - {displayName}
                        </span>
                    ))}
                </Alert>, { autoHideDuration: 15000 }
            );
        }
        return result.isValid;
    }
    //#endregion

    //#region SAVE ORDER
    const onChangeOrder = (name, value) => {
        setOrderData((prev) => ({ ...prev, [name]: value }));
        if (name === 'primary_account') {
            setTimeout(() => {
                setDlgState(prev => ({ ...prev, valid_pa_email: !value ? true : isValidEmail(value) }));
            }, 1000);
        }
        config();
    };

    function isDelayDateInvalid (delay?) {
        let delayDate = dayjs(delay ?? orderData?.delay_date);
        let inhandDate = dayjs(orderData?.in_hand_date);
        return delayDate.isAfter(inhandDate);
    }

    function alertDelayDate () {
        notify(
            <Alert severity="error">
                <span style={{ fontWeight: 'bold' }}>Invalid Input!</span><br />
                {`Please set delay date before expected date (${dayjs(orderData.in_hand_date).format('MM/DD/YYYY')})`}<br />
            </Alert>,
            { autoHideDuration: 5000 }
        );
    }

    /**
     * Creates a deferred promise object with resolve and reject functions.
     * This allows manual control over the promise's resolution or rejection.
     * @returns {{promise: Promise, resolve: function, reject: function}}
     */
    function createDeferredPromise () {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    }

    const onSaveOrder = async (orderView?) => {
        setSaving(true);
        /**
         * Updates the actor (shipper or processor) for the current order if the original value is null or auto-assigned.
         * This function is used to automatically assign the current user to an actor field when saving an order,
         * but only if the field is empty or contains an auto-assigned value.
         *
         * @param {string|null} originalVal - The original actor value (shipper or processor) to check
         * @param {string} actorType - The type of actor to update: 'shipper' or 'processor'
         */
        function changeActor (originalVal, actorType) {
            // Check if originalVal is null/undefined or contains 'auto' (case-insensitive)
            if (!originalVal || originalVal.toLowerCase().includes('auto')) {
                // Update the appropriate actor field based on actorType parameter
                orderData[actorType] = currUser;
            }
        }

        let primaryOrderID = orderData.primary_order_number;
        let match = /^\s*([0-9]+-[0-9]+\/[A-Z0-9]{3}).*/.exec(primaryOrderID);
        if (match != null && match[0] !== match[1]) {
            notify('ERROR: Invalid Primary Order ID', { type: 'error' });
            setSaving(false);
            return { success: false, status: 'VALIDATION_FAILED', message: 'Invalid Primary Order ID' };
        }

        if (parseInt(orderData.internal_status) === STATUS_ENUM.Completed) {
            changeActor(orderData.shipper, 'shipper');
        } else if (parseInt(orderData.internal_status) === STATUS_ENUM.PendingShipment) {
            changeActor(orderData.processor, 'processor');
        }
        // "saveOrder": specific validating order when click SAVE button
        let displayed = validateDialogFixed('saveOrder');

        // Validate status change from Processing to Unprocessed
        if (!validProcessingToUnprocessed()) {
            setSaving(false);
            return { success: false, status: 'VALIDATION_FAILED', message: 'Processing to Unprocessed validation failed' };
        }

        let internal_status = orderData.internal_status;
        if (parseInt(internal_status) === STATUS_ENUM.PendingShipment) {
            if (!validPurchase()) {
                setSaving(false);
                return { success: false, status: 'VALIDATION_FAILED', message: 'Purchase validation failed' };
            }
        }
        else if (parseInt(internal_status) === STATUS_ENUM.Completed) {
            if (!validComplete()) {
                setSaving(false);
                return { success: false, status: 'VALIDATION_FAILED', message: 'Completion validation failed' };
            }
        }

        if (isDelayDateInvalid()) {
            alertDelayDate();
            setSaving(false);
            return { success: false, status: 'VALIDATION_FAILED', message: 'Invalid delay date' };
        }

        if (displayed) {
            // Use deferred promise to wait for user confirmation from dialog: improve for call onSaveOrder from outside
            const deffered = createDeferredPromise();
            saveOrderDeferredRef.current = { resolve: deffered.resolve, reject: deffered.reject };
            setDlgState(prev => ({ ...prev, open: true, orderView: orderView }));
            setSaving(false);
            return deffered.promise;
        }

        return orderView ? await updateOrder(orderView) : updateOrder();
    };

    function updateOrder (orderView?, latestUpdateOrderData?) {
        setSaving(true);
        let dataToUpdate = latestUpdateOrderData || orderData;
        // for process tracking
        let _requiredProcess = onConfigTrackingProcess(initialOrder, dataToUpdate, notes, countProcessingAttempts(notes));
        if (_requiredProcess.required) {
            return new Promise((resolve, reject) => {
                setDlgState((prev) => ({
                    ...prev, h_isOpen: true, h_isEnable: true,
                    callback: (data) => {
                        if (data) onChangeOrder('orderProcessTracking', data);
                        let latestSaveOrderData = { ...dataToUpdate, orderProcessTracking: data };
                        // Call saveOrder and use the resolve/reject of the outer Promise
                        saveOrder(orderView, latestSaveOrderData).then(resolve).catch(reject);
                    }
                }));
            });
        }
        // for tracking mapping
        let _requiredMapping = onConfigTrackingMapping(dataToUpdate, initialOrder.tags);
        if (_requiredMapping) {
            return new Promise((resolve, reject) => {
                setDlgState((prev) => ({
                    ...prev, m_isOpen: true, m_data: { oldTags: initialOrder.tags },
                    callback: (data) => {
                        let latestSaveOrderData = { ...dataToUpdate, mapping_error: data };
                        // Call saveOrder and use the resolve/reject of the outer Promise
                        saveOrder(orderView, latestSaveOrderData).then(resolve).catch(reject);
                    }
                }));
            });
        }

        let unableToProcess = checkAddUnableToProcessTag(dataToUpdate);
        if (unableToProcess) {
            return new Promise((resolve, reject) => {
                setSaving(true);
                setDlgState((prev) => ({
                    ...prev,
                    unable_to_process_isOpen: true,
                    unable_to_process_addTag: true,
                    control: 'unable_to_process',
                    callback: (updatedTags) => saveOrder(orderView, { ...dataToUpdate, tags: updatedTags }).then(resolve).catch(reject)
                }));
            });
        }

        return saveOrder(orderView, dataToUpdate);
    }

    function checkAddUnableToProcessTag (latestOrderData?) {
        let checkingOrder = latestOrderData || orderData;
        // Skip if existing TAGS_IGNORED
        const TAGS_IGNORED = ['Problem', 'Order Support'];
        const STATUS_ALLOWED = [STATUS_ENUM.Unprocessed, STATUS_ENUM.Processing, STATUS_ENUM.TNProcessing];
        if (checkingOrder.tags && TAGS_IGNORED.some(tag => checkingOrder.tags.includes(tag))) {
            return false;
        }
        // Check if current status is allowed
        const currentStatus = parseInt(checkingOrder.internal_status);
        if (!STATUS_ALLOWED.includes(currentStatus)) {
            return false;
        }

        // Only check attempts if 'Unable To Process' tag does NOT exist
        const tags = (checkingOrder.tags || '').split(',').map((tag: any) => tag.trim());
        if (!tags.includes(unableToProcessTag)) {
            const attemptCount = countProcessingAttempts(notes);
            if (attemptCount > 2) {
                // Move save action outside to show dialog
                return true;
            }
        }
        return false;
    }

    /**
     * Reveals customer information for Direct Transfer and Flash orders after shipping
     * and creates a system note with customer details.
     * This function is called when the shipping button is successfully pressed.
     *
     * @param {Object} customerData - Customer information to reveal and log
     * @param {string} customerData.name - Customer name
     * @param {string} customerData.email - Customer email
     * @param {string} [originalEmail] - Original email for Vivid orders when email was updated
     */
    const revealCustomerInfoAndCreateNote = (customerData, originalEmail = null) => {
        // Reveal customer information
        setShowCustomerInfo(true);

        // Create system note with customer information
        if (customerData && customerData.name && customerData.email) {
            let noteContent = `Customer Name: ${customerData.name}\nEmail: ${customerData.email}`;
            // For Vivid orders with email updates, include both original and updated emails
            if (originalEmail && originalEmail !== customerData.email) {
                noteContent = `Customer Name: ${customerData.name}\nOriginal Email: ${originalEmail}\nUpdated Email: ${customerData.email}`;
            }
            // Create system note via backend
            dataProvider
                .postData(vendor.controller, 'Note', {
                    order_id: orderData.id,
                    order_number: orderData.order_number,
                    post: noteContent,
                    note_type: 1 // System note (updated from -1 to 1)
                })
                .then((rs: any) => {
                    if (rs.data) {
                        loadNotes(true); // Reload notes to show the new system note
                        notify('Customer information revealed and logged', { type: 'success', autoHideDuration: 2000 });
                    }
                })
                .catch((error: any) => {
                    notify(`Failed to create customer info note: Status ${error.status}, Error ${error}`, { type: 'error', autoHideDuration: 3000 });
                });
        }
    };

    function saveOrder (recordToSave?, latestOrderData?) {
        let dataToSave = latestOrderData || orderData;
        let refreshNotesAfterSave = false;
        return dataProvider.postData(vendor.controller, 'Update', dataToSave)
            .then((res: any) => {
                if (res.data) {
                    let response = res.data;
                    if (response.msg) {
                        notify(`${response.success ? 'WARNING' : 'ERROR'}: ${response.msg}`, { type: `${response.success ? 'warning' : 'error'}` });
                        if (response.msg.includes('Someone has changed the order')) {
                            setEffectData((prev) => ({ ...prev, isAlert: true }));
                        }
                    }
                    if (response.success) {
                        notify('Order saved successfully', { type: 'success', autoHideDuration: 2000 });
                        if (!!response.msg) {
                            notify(`Server message: ${response.msg}`, { type: 'warning', autoHideDuration: 10000 });
                        }

                        if (response.msg.includes('RELOAD')) {
                            setDlgState(prev => ({ ...prev, reload_order: !prev.reload_order }));
                            return { success: true, status: 'RELOAD', data: response.order };
                        }
                        //update OldOrder
                        setInitialOrder(response.order);
                        //update order data
                        let newData = { ...dataToSave };
                        newData.currentStatus = newData.internal_status;
                        newData.utc_last_updated_on = response.order.utc_last_updated_on;

                        let _notes = response.order.order_notes || [];
                        newData.order_notes = _notes.reduce(function (prev: any, cur: any) { if (prev.length < 3 && cur.note_type === 0) prev.push(cur); return prev; }, []);
                        newData.order_fullnotes = _notes;
                        newData.isRowSpec = response.order.isRowSpec;
                        //!Auto-save revert status
                        newData.allowRevertStatus = recordToSave?.allowRevertStatus || false; // Reset allowRevertStatus to false after saving
                        // update primary cost if auto conversion happened in backend
                        newData.primary_cost = response.order.primary_cost;
                        if (dataToSave.primary_cost && dataToSave.primary_cost !== response.order.primary_cost) {
                            refreshNotesAfterSave = true;
                        }
                        bus.broadcast(bus_events.order_saved, newData);
                        // Update the order data state with the new data
                        setOrderData(newData);

                        return { success: true, status: 'SUCCESS', data: newData };
                    } else {
                    }
                }
                else {
                    notify(`Something is wrong: [${res?.message || res?.status}].`, { type: 'error' });
                    return { success: false, status: 'ERROR', message: res?.message || res?.status || 'Unknown error' };
                }
            })
            .catch((error: any) => {
                console.error('Save order failed:', error);
                return { success: false, status: 'ERROR', message: error?.message || error?.status || 'Unknown error' };
            })
            .finally(() => {
                if (refreshNotesAfterSave) {
                    setTimeout(() => {
                        notify('Primary cost converted to USD. Notes reloaded.', { type: 'success', autoHideDuration: 3000 });
                    }, 2000);
                    loadNotes();
                }
                setDlgState(prev => ({ ...prev, control: '' }));
                setSaving(false);
            });
    }

    const handleValidateConfirm = async () => {
        const { orderView } = dlgState;
        setDlgState(prev => ({ ...prev, open: false, content: [], orderView: null }));
        switch (dlgState.control) {
            case 'saveOrder':
                let isChangeDeliveryMethod = extractTextFromReactNode(dlgState.content).toLowerCase().includes('send an email to portal');
                let newAllowNotify = isChangeDeliveryMethod ? dlgState.sendEmail : orderData.allowNotify;
                let updatedOrderInput = { ...orderData, allowNotify: newAllowNotify };
                setOrderData(updatedOrderInput);
                await updateOrderWithPromise(orderView, updatedOrderInput);
                break;
            case 'shipping':
                setDlgState(prev => ({ ...prev, openShipping: true }));
                break;
            case 'unable_to_process':
            // Handle "Unable To Process" tag addition/removal based on checkbox state
                let currentTags = orderData.tags ? orderData.tags.split(',').filter((tag: any) => tag.trim()) : [];

                if (dlgState.unable_to_process_addTag) {
                // Add tag if not already present
                    if (!currentTags.includes(unableToProcessTag)) {
                        currentTags.push(unableToProcessTag);
                    }
                } else {
                // Remove tag if present
                    currentTags = currentTags.filter((tag: any) => tag !== unableToProcessTag);
                }

                // Update the order tags
                let updatedTags = currentTags.join(',');
                onChangeOrder('tags', updatedTags);

                // Close the dialog and proceed with save
                setDlgState(prev => ({ ...prev, unable_to_process_isOpen: false }));
                if (dlgState.callback) {
                    dlgState.callback(updatedTags);
                }
                break;
            default:
                setDlgState(prev => ({ ...prev, needsSave: true }));
                break;
        }
        // Improve for call onSaveOrder from outside
        async function updateOrderWithPromise (orderView, updateOrderInput) {
            if (saveOrderDeferredRef.current) {
                try {
                    const result = orderView ? await updateOrder(orderView, updateOrderInput) : updateOrder(undefined, updateOrderInput);
                    saveOrderDeferredRef.current.resolve(result);
                } catch (error) {
                    console.error('[handleValidateConfirm] - Error updating order:', error);
                    saveOrderDeferredRef.current.reject(error);
                }
            }
        }
    };


    /**
     * Counts the number of processing attempts for an order.
     * A processing attempt is identified by a user note (note_type 0 or -99, with an email, and not containing any IGNORED_AUTO_NOTES keywords)
     * whose post contains any of the STATUS_CHANGE_NOTES (e.g., 'Changed status to Processing', 'Changed status Unprocessed to Processing').
     * The function counts:
     *   - All user notes (type 0 or -99, with email, not auto/system notes)
     *   - All status change notes (user notes whose post contains any STATUS_CHANGE_NOTES)
     * Returns the number of status change attempts only if:
     *   - There are at least 2 status change notes
     *   - The number of user notes is at least as many as the number of status change notes
     * Otherwise, returns 0.
     *
     * @param {Array} notes - Array of order notes. Each note should have: note_type, post, email.
     * @returns {number} Number of valid processing attempts (status changes) found per business rules.
     */
    function countProcessingAttempts (notes) {
        if (!Array.isArray(notes) || notes.length === 0) return 0;

        const IGNORED_AUTO_NOTES = [
            'Clicked process button',
            'SEC ',
            'PRIMARY ',
            'Changed in-hand-date',
            'Shipped ',
            'PROCESS TRACKING'
        ];

        let statusChangeAttempts = 0;
        let userNoteCount = 0;

        const STATUS_CHANGE_NOTES = ['Changed status to Processing', 'Changed status Unprocessed to Processing'];
        for (const note of notes) {
            if (
                note.email?.includes('@') && !IGNORED_AUTO_NOTES.some(ignored => note.post.includes(ignored))
            ) {
                if (note.note_type === 0 || note.note_type === -99) {
                    userNoteCount++;
                }
                if (STATUS_CHANGE_NOTES.some(x => note.post.includes(x))) {
                    statusChangeAttempts++;
                }
            }
        }

        let minAttempts = 2;
        // Only count if more than 2 status changes and at least as many user notes as status changes
        return (statusChangeAttempts > minAttempts && userNoteCount >= minAttempts)
            ? statusChangeAttempts
            : 0;
    }
    //#endregion

    const MuiFormLabel = (props: any) => {
        const { text } = props;
        return (
            <span className="MuiFormLabel-root control-label">{text}</span>
        );
    };

    const AlertOrderChanged = (props: any) => {
        let { by } = props;
        by = showOrderLastChangedBy ?? by ?? 'Someone else';
        return (
            <div name="refresh-after-order-changed" className="alert alert-info">
                <button
                    type="button"
                    onClick={() => {
                        setDlgState(prev => ({ ...prev, reload_order: !prev.reload_order }));
                        setEffectData((prev) => ({ ...prev, isAlert: false, showOrderLastChangedBy: undefined }));
                    }}
                    style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#0d6efd',
                        font: 'inherit'
                    }}
                >
                    <b>{by}</b> has just updated the order, Please click here to discard your changes, and keep remote ones or click on the button Save to discard remote changes, and keep your changes. Thank you!
                </button>
            </div>
        );
    };

    let location = window.location;
    return (
        <>
            {orderData &&
                <Grid container className="Form-Order" columnSpacing={1} sx={{ minHeight: '650px' }}>
                    {isAlert && <Grid container columnSpacing={2} sx={{ width: '100%' }}>
                        <Grid size={12}>
                            <AlertOrderChanged by={orderData.processor} />
                        </Grid>
                    </Grid>}
                    {/* LEFT GROUP */}
                    <Grid rowSpacing={1.5} size={8} style={{ pointerEvents: isLoading ? 'none' : '', opacity: isLoading ? '0.5' : '' }}>
                        {isLoading && <LinearProgress color='secondary' sx={{ marginBottom: 1 }} />}
                        <Grid container columnSpacing={2} className="order-id-area" size={12} margin={'10px 0px'}>
                            <Grid size={3} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                <MuiFormLabel text='Order ID' />
                            </Grid>
                            <Grid container size={9}>
                                <Grid container columnSpacing={2} size={7}>
                                    <Grid size={2}><Typography>{usesStrOrderNumber ? orderData?.str_order_number : orderData?.order_number}</Typography></Grid>
                                    <Grid size={10}>
                                        <Button variant="outlined" size="small" style={{ marginRight: '3px' }} href={`${location.origin}/Listings/?ListingId=${orderData?.ticket_id}&account_id_eq=${orderData?.vivid_account_id}&action=&refreshAgo=&is_shown_eq=null`} target="_blank">Listing</Button>
                                        <Button variant="outlined" size="small" style={{ marginRight: '3px' }} href={`${location.origin}/ListingsMapping/?TicketId=${orderData?.ticket_id}&MarketId=${orderData?.market_id}`} target="_blank">Mapping</Button>
                                        <Button variant="outlined" size="small" style={{ marginRight: '3px' }} href={`${location.origin}/EventDelivery/?SiteId=${orderData?.event_site_id}`} target="_blank">EDT</Button>
                                        <DMQPopup {...sharedDialogProps} />
                                    </Grid>
                                </Grid>
                                <Grid container columnSpacing={2} size={5}>
                                    <Grid size={2}> <MuiFormLabel text='Account' /></Grid>
                                    <Grid size={10}> <Typography>{isResale ? orderData.vivid_account : accounts.find((acc: any) => acc.id === orderData.vivid_account_id)?.name}</Typography></Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Primary Order ID' />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField size="small" hiddenLabel margin="dense" variant="outlined" value={orderData?.primary_order_number ?? ''} onChange={(props) => onChangeOrder('primary_order_number', props.target.value)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2} >
                                    <Grid size={4.5} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Primary Account' />
                                    </Grid>
                                    <Grid container size={7.5} columnSpacing={0.5}>
                                        <Grid size={11}>
                                            <TextField value={orderData?.primary_account ?? ''} size="small" hiddenLabel margin="dense" variant="outlined" error={!dlgState.valid_pa_email} onChange={(props) => onChangeOrder('primary_account', props.target.value)} />
                                        </Grid>
                                        <Grid size={1}>
                                            <IconButton title="Suggest" size="small" sx={{ marginRight: '3px', borderRadius: '4px' }} onClick={() => setDlgState((prev) => ({ ...prev, s_isOpen: true }))}><SavedSearchOutlined /></IconButton>
                                        </Grid>
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12} sx={{ marginBottom: '14px' }}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Penalty Cost' />
                                    </Grid>
                                    <Grid size={6}>
                                        <MNumber isNegative={false} decimal={2} value={orderData?.penalty_cost ?? ''} onChange={(value) => onChangeOrder('penalty_cost', value)} size="small" hiddenLabel margin="dense" variant="outlined" startAdornment={<InputAdornment position="start">$</InputAdornment>} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Primary Cost' />
                                    </Grid>
                                    <Grid size={6}>
                                        <MNumber isNegative={false} decimal={2} value={orderData?.primary_cost ?? ''} onChange={(value) => onChangeOrder('primary_cost', value)} size="small" hiddenLabel margin="dense" variant="outlined" startAdornment={<InputAdornment position="start">$</InputAdornment>} />
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12} sx={{ marginBottom: '15px', marginTop: '20px' }}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Processor' />
                                    </Grid>
                                    <Grid size={6}>
                                        <Typography style={{ color: 'gray' }}>{orderData?.processor || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Payment' />
                                    </Grid>
                                    <Grid size={4}>
                                        <Typography style={{ color: 'gray' }}>{orderData?.payment_status || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Shipper' />
                                    </Grid>
                                    <Grid size={2}>
                                        <Typography style={{ color: 'gray' }}>{orderData?.shipper || 'Shipper'}</Typography>
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Date Expected' />
                                    </Grid>
                                    <Grid size={6} className="datepicker-padding">
                                        <MDatePicker placeholder={'Date Expected'} variant="outlined" value={orderData?.in_hand_date} onChange={(value) => onChangeOrder('in_hand_date', value)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='CB Date' />
                                    </Grid>
                                    <Grid size={4} className="datepicker-padding">
                                        <MDatePicker placeholder={'Check back date'} variant={'outlined'} value={orderData?.checkback_date} onChange={(value) => onChangeOrder('checkback_date', value)} />
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Status' />
                                    </Grid>
                                    <Grid size={4} sx={{ alignContent: 'center' }} className="mselect-padding">
                                        <MSelect variant={'outlined'} className='mselect-small' name="internal_status" choices={allowEditStatuses} value={orderData?.internal_status ?? ''} onChange={(value) => onChangeOrder('internal_status', value)} />
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Tags' />
                                    </Grid>
                                    <Grid size={6}>
                                        <MAutoComplete id="mautocomplete" name='tags' variant={'outlined'} defaultValue={orderData?.tags.split(',').filter((x: any) => x)}
                                            choices={filterTags} getStyles={getTagsColor} className="" onChange={(values) => {
                                                onChangeOrder('tags', values.join(','));
                                                setDlgState((prev) => ({ ...prev, btn_problemTracking: onConfigTrackingProblem(initialOrder, values) }));
                                            }}
                                        // sx={{'& .MuiChip-label': {paddingLeft: "1px", paddingRight: "4px"}, '& .MuiSvgIcon-root': {marginRight: "0px"}}}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Unit Price' />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField value={orderData?.unit_list_price ?? ''} onChange={(props) => onChangeOrder('unit_list_price', props.target.value)} sx={{ color: 'white' }} disabled hiddenLabel size="small" margin="dense" variant="outlined" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Order Total' />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField value={orderData?.order_total ?? ''} onChange={(props) => onChangeOrder('order_total', props.target.value)} disabled size="small" hiddenLabel margin="dense" variant="outlined" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Section' />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField value={orderData?.section ?? ''} disabled size="small" hiddenLabel margin="dense" variant="outlined" onChange={(props) => onChangeOrder('section', props.target.value)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Quantity' />
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField value={orderData?.quantity ?? ''} onChange={(props) => onChangeOrder('quantity', props.target.value)} disabled size="small" hiddenLabel margin="dense" variant="outlined" />
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center', color: orderData.isRowSpec ? '#d9534f' : '' }}>
                                        <MuiFormLabel text={orderData.isRowSpec ? 'Row Spec' : 'Row'} />
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField value={orderData?.row ?? ''} onChange={(props) => onChangeOrder('row', props.target.value)} disabled size="small" hiddenLabel margin="dense" variant="outlined" />
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Seat' />
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField value={orderData?.seat ?? ''} disabled size="small" hiddenLabel margin="dense" variant="outlined" onChange={(props) => onChangeOrder('seat', props.target.value)} />
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Section' />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField value={orderData?.purchase_section ?? ''} name="section" onChange={(props) => onChangeOrder('purchase_section', props.target.value)} size="small" hiddenLabel margin="dense" variant="outlined" />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Quantity' />
                                    </Grid>
                                    <Grid size={2}>
                                        <MNumber decimal={2} value={orderData?.purchase_quantity ?? ''} range={[1]} onChange={(value) => onChangeOrder('purchase_quantity', value)} size="small" hiddenLabel margin="dense" variant="outlined" />
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Row' />
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField value={orderData?.purchase_row ?? ''} onChange={(props) => onChangeOrder('purchase_row', props.target.value)} size="small" hiddenLabel margin="dense" variant="outlined" />
                                    </Grid>
                                    <Grid size={2} >
                                        <TextField value={orderData?.lo_seat ?? ''} onChange={(props) => onChangeOrder('lo_seat', props.target.value)} size="small" hiddenLabel margin="dense" variant="outlined" placeholder="Lo Seat" />
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField value={orderData?.hi_seat ?? ''} onChange={(props) => onChangeOrder('hi_seat', props.target.value)} size="small" hiddenLabel margin="dense" variant="outlined" placeholder="Hi Seat" />
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12} marginTop={'10px'}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Delay Date' />
                                    </Grid>
                                    <Grid size={6} className="datepicker-padding">
                                        <MDatePicker placeholder={'Delay Date'} variant="outlined" value={orderData?.delay_date} onChange={(formatedValue) => onChangeOrder('delay_date', formatedValue)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    {dlgState.d_isEnable && (
                                        <>
                                            <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                                <MuiFormLabel text='Delivery delay' />
                                            </Grid>
                                            {[1, 2, 3, 7, 10].map((e, index) => {
                                                let option = e === 1 ? ' day' : ' days';
                                                return (
                                                    <Grid size={2} key={`ddDay-${orderData.order_number}-${index}`}>
                                                        <Button variant="outlined" size="small" onClick={() => onClickDeliveryDelay(e, 'd_isOpen')}>{e + option}</Button>
                                                    </Grid>
                                                );
                                            })}
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Delivery Method' />
                                    </Grid>
                                    <Grid size={6} className="mselect-padding">
                                        <MSelect variant="outlined" className='mselect-small' choices={DeliveryMethodEnum.map((x: any) => ({ ...x, id: parseInt(x.id) }))} value={orderData?.delivery_method ?? ''}
                                            onChange={(value) => onChangeOrder('delivery_method', value)}
                                            sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: `${DeliveryMethodColors.find((x: any) => x.id === orderData.delivery_method)?.color || '#ffffff00'}` } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={4}>
                                        {(forceRender.isAvailableUnshipped || isUploadPOA()) &&
                                            <OrderShipping
                                                notify={notify}
                                                cur_delivery_method={orderData?.delivery_method}
                                                dataProvider={dataProvider}
                                                vendor={vendor}
                                                validate={validateDialogFixed}
                                                setDlgState={setDlgState}
                                                validComplete={validComplete}
                                                isUploadPOA={isUploadPOA()}
                                                openShipping={dlgState.openShipping}
                                                closePopup={() => setDlgState(prev => ({ ...prev, openShipping: false }))}
                                                customer={customer}
                                                revealCustomerInfo={revealCustomerInfoAndCreateNote}
                                                saveOrder={saveOrder}
                                                orderData={orderData}
                                                setOrderData={setOrderData}
                                            />}
                                        {forceRender.isUploadProof && initialOrder.event_market === 'Exact' && <ProofAcceptance notify={notify} dataProvider={dataProvider} vendor={vendor} setDlgState={setDlgState} orderData={orderData} />}
                                    </Grid>
                                    <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='LTP' />
                                    </Grid>
                                    <Grid size={6} >
                                        <TextField size="small" onChange={(props) => onChangeOrder('link_to_print_tickets', props.target.value)} value={orderData?.link_to_print_tickets ?? ''} hiddenLabel margin="dense" variant="outlined" placeholder="Link to Print Tickets" />
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2} size={12}>
                            <Grid size={6}>
                                <Grid container columnSpacing={2}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Price Level' />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField value={orderData?.pricelevel ?? ''} size="small" hiddenLabel margin="dense" variant="outlined" disabled onChange={(props) => onChangeOrder('pricelevel', props.target.value)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={6}>
                                <Grid container columnSpacing={2} sx={{ marginTop: '1px' }}>
                                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                        <MuiFormLabel text='Process Tracking' />
                                    </Grid>
                                    <Grid size={6} sx={{ display: 'flex' }}>
                                        {/* https://analysis.songtek.net/ProcessTrackingReport?event=Hamilton&venue=Eccles%20Theater%20-%20Salt%20Lake%20City%20-%20Salt%20Lake%20City,%20UT&Type_eq=arttix */}
                                        <IconButton size="small" sx={{
                                            background: '#5cc0de', '&:hover': { backgroundColor: '#0099ff' }, borderRadius: '4px'
                                        }} target="_blank" title="Lookup history"
                                        href={`${location.origin}/ProcessTrackingReport?utc_Posted_On=["${dayjs().add(-2, 'M').format('YYYY-MM-DD')}"]&venue=${encodeURIComponent(orderData?.info?.venue).replace(/\./g, '%2E')}&type_eq=${orderData?.event_market?.toLowerCase()}`}
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                        {(dlgState.h_isEnable || dlgState.h_isManual) && (
                                            <IconButton
                                                title={dlgState.h_isEnable ? 'Tracking' : 'Add Manual Entry'}
                                                size="small"
                                                sx={{
                                                    marginLeft: '3px',
                                                    background: 'orange',
                                                    '&:hover': { backgroundColor: '#e67300' },
                                                    borderRadius: '4px',
                                                }}
                                                onClick={() =>
                                                    setDlgState(prev => ({
                                                        ...prev,
                                                        h_isOpen: true,
                                                        h_isSaveOrder: false
                                                    }))
                                                }
                                            >
                                                {dlgState.h_isEnable ? <Storage /> : (dlgState.h_isManual ? <AddIcon /> : <Storage />)}
                                            </IconButton>
                                        )}
                                        {orderData?.isMappingError && <IconButton title="Mapping Error" size="small"
                                            sx={{
                                                borderRadius: '4px',
                                                background: '#d9534f', '&:hover': {
                                                    backgroundColor: '#c9302c',
                                                },
                                                marginLeft: '3px'
                                            }}
                                            target="_blank"
                                            href={`${location.origin}/MappingErrorReport?order_number_eq=${orderData?.order_number}`} >
                                            <FmdBadOutlined />
                                        </IconButton>}
                                        {dlgState.btn_problemTracking && <TrackingProblemPopup {...sharedDialogProps} callback={(data) => onChangeOrder('orderProblemTracking', data)} />}
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        {
                            isResale &&
                            <Grid container columnSpacing={2} size={12}>
                                <Grid size={6}>
                                    <Grid container columnSpacing={2}>
                                        <Grid size={6} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                            <MuiFormLabel text='Event Market' />
                                        </Grid>
                                        <Grid size={6} className="mselect-padding">
                                            <MSelect variant="outlined" className='mselect-small' choices={marketType} value={orderData?.event_market ?? ''}
                                                onChange={(value) => onChangeOrder('event_market', value)}
                                                ignoreCaseSensitive={true}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid size={6}>
                                    <Grid container columnSpacing={2}>
                                        <Grid size={2} sx={{ textAlign: 'right', alignContent: 'center' }}>
                                            <MuiFormLabel text='Event Url' />
                                        </Grid>
                                        <Grid size={10} >
                                            <TextField size="small" onChange={(props) => onChangeOrder('event_url', props.target.value)} value={orderData?.event_url ?? ''} hiddenLabel margin="dense" variant="outlined" placeholder="Primary or Secondary Url" />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                        <Grid container columnSpacing={2} size={12} className="notes-button" sx={{ marginTop: '10px' }}>
                            <Grid size={3} sx={{ textAlign: 'right' }}>
                                <MuiFormLabel text='Notes' />
                            </Grid>
                            <Grid size={6} >
                                <Typography>{orderData?.listing_notes}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={0.5} size={12} className="save-group-button" justifyContent={'flex-end'} sx={{ marginTop: '10px' }}>
                            <Grid>
                                {forceRender.isWillCall && <MoreDetailPopup {...sharedDialogProps} />}
                            </Grid>
                            <Grid>
                                <Button className="dark-primary-button"
                                    variant="contained"
                                    size="small"
                                    ref={refbtnSave}
                                    disabled={!initialOrder}
                                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    onClick={async () => {
                                        // turn on save Order for tracking process
                                        setDlgState((prev) => ({ ...prev, h_isSaveOrder: true, needsSave: true })); // needsSave to trigger useEffect to save order
                                    }}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* RIGHT GROUP */}
                    <Grid size={4} style={{ paddingLeft: '20px', pointerEvents: !notes ? 'none' : '', opacity: !notes ? '0.5' : 1 }}>
                        <OrderNotesDetails
                            vendor={vendor}
                            dataProvider={dataProvider}
                            notes={notes}
                            order={orderData}
                            customer={customer}
                            showCustomerInfo={showCustomerInfo}
                            convertNote={{ isSaveNote: orderData.isSaveNote, newNote: orderData.newNote }}
                            loadNotes={loadNotes}
                            callback={() => {
                                loadNotes(true);
                                let unableToProcess = checkAddUnableToProcessTag();
                                if (unableToProcess) {
                                    setSaving(true);
                                    setDlgState((prev) => ({
                                        ...prev,
                                        unable_to_process_isOpen: true,
                                        unable_to_process_addTag: true,
                                        control: 'unable_to_process',
                                        callback: () => saveOrder()
                                    }));
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            }
            {dlgState.s_isOpen && <SuggestAccountsPopup {...sharedDialogProps} onChange={(resp) => {
                onChangeOrder('primary_account', resp.primaryAccount);
                const tagValues = resp.account_Tags;
                if (tagValues) {
                    const currentTags = orderData.tags ? orderData.tags.split(',').map((t: any) => t.trim()).filter(Boolean) : [];
                    const newTags = tagValues.split(',').map((t: any) => t.trim()).filter(Boolean);
                    const addedTags = newTags.filter((tag: any) => !currentTags.includes(tag));
                    const distinctTags = Array.from(new Set([...currentTags, ...newTags])); // ensure tags are unique
                    if (addedTags.length > 0) {
                        notify(`Added tags: ${addedTags.join(', ')}`, { type: 'success' });
                    }
                    onChangeOrder('tags', distinctTags.join(','));
                    setDlgState((prev) => ({ ...prev, btn_problemTracking: onConfigTrackingProblem(initialOrder, distinctTags) }));
                }
                // Privacy.com (PVC) accounts: resume the matching card(s) immediately and let the
                // backend auto re-pause them 10 minutes later (CreditCardService.ResumeWithAutoPauseAsync).
                // Re-selecting the same PVC account before the 10 minutes elapse resets that countdown
                // server-side, so this call is safe to fire on every selection with no manual step.
                if (resp.cC_Type?.toLowerCase().includes('(pvc)')) {
                    dataProvider.postData('CreditCards', 'ResumeWithAutoPause', resp.primaryAccount)
                        .then((response: any) => {
                            // `postData` swallows HTTP errors into a resolved `HttpError` instead of
                            // rejecting (see PauseResumeAction.tsx), so `response?.data` must be checked
                            // explicitly rather than relying on the promise resolving.
                            if (!response?.data) {
                                notify(`Failed to resume PVC card: ${response?.message || response}`, { type: 'error', autoHideDuration: 5000 });
                            } else {
                                notify(response.data.message, { type: response.data.success ? 'success' : 'warning', autoHideDuration: 5000 });
                            }
                        })
                        .catch((error: any) => {
                            notify(`Failed to resume PVC card: ${error.message || error}`, { type: 'error', autoHideDuration: 5000 });
                        });
                }
            }} />}
            {dlgState.d_isOpen && <DeliveryDelayPopup {...sharedDialogProps} executeSaveClick={() => refbtnSave.current.click()} />}
            {dlgState.h_isOpen && <OrderProcessTrackingPopup {...sharedDialogProps} callback={(data) => onChangeOrder('orderProcessTracking', data)} />}
            {dlgState.m_isOpen && <TrackingMappingPopup {...sharedDialogProps} />}
            <Confirm isOpen={dlgState.unable_to_process_isOpen}
                aria-label="unable-to-process-confirm"
                title="Order Processing Attempts Exceeded"
                content={
                    <div>
                        <p>This order has been attempted to process more than 3 times and is being changed back to "Unprocessed" status.</p>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={dlgState.unable_to_process_addTag}
                                    onChange={(e) => setDlgState(prev => ({ ...prev, unable_to_process_addTag: e.target.checked }))}
                                    color="primary"
                                />
                            }
                            label="Add 'Unable To Process' tag to this order"
                        />
                    </div>
                }
                onClose={() => {
                    setDlgState(prev => ({ ...prev, unable_to_process_isOpen: false }));
                    setSaving(false);
                }}
                onConfirm={handleValidateConfirm}
                confirmColor="primary" />
            <Confirm isOpen={dlgState.open}
                aria-label="open"
                title={['shipping', 'saveOrder'].some(x => x === dlgState.control) ? 'WARNING! DO YOU WANT SAVE ANY WAY?' : ''}
                content={dlgState.content}
                onClose={(e) => onCloseDialog(e, 'open')}
                onConfirm={handleValidateConfirm}
                confirmColor="primary" />
        </>
    );
}));

export default OrderDetails;
